/**
 * WeaveIn.ai Platform - Unified Field Service
 * Single source of truth for all field definitions across workflows, forms, and member profiles
 *
 * This service ensures that all systems (Form Builder, Workflows, Member Profiles, Integration Workbench)
 * use the same field configuration and definitions.
 */

import type { CustomField, FieldType, Member } from './api/members';

// ============================================================================
// CORE FIELDS DEFINITION (Hardcoded system fields)
// ============================================================================

export interface CoreFieldDefinition {
  name: string;
  type: FieldType;
  friendly_name: string;
  category: 'core';
  mandatory: boolean;
  nullable: boolean;
  description?: string;
}

export const CORE_FIELDS: CoreFieldDefinition[] = [
  {
    name: 'first_name',
    type: 'str',
    friendly_name: 'First Name',
    category: 'core',
    mandatory: true,
    nullable: false,
    description: 'Member first name',
  },
  {
    name: 'last_name',
    type: 'str',
    friendly_name: 'Last Name',
    category: 'core',
    mandatory: true,
    nullable: false,
    description: 'Member last name',
  },
  {
    name: 'email',
    type: 'str',
    friendly_name: 'Email',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member email address',
  },
  {
    name: 'phone_no',
    type: 'str',
    friendly_name: 'Phone Number',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member phone number',
  },
  {
    name: 'dob',
    type: 'date',
    friendly_name: 'Date of Birth',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member date of birth',
  },
  {
    name: 'gender',
    type: 'str',
    friendly_name: 'Gender',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member gender',
  },
  {
    name: 'race',
    type: 'str',
    friendly_name: 'Race',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member race',
  },
  {
    name: 'ethnicity',
    type: 'str',
    friendly_name: 'Ethnicity',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member ethnicity',
  },
  {
    name: 'language',
    type: 'str',
    friendly_name: 'Preferred Language',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member preferred language',
  },
  {
    name: 'patient_no',
    type: 'str',
    friendly_name: 'Patient Number',
    category: 'core',
    mandatory: false,
    nullable: true,
    description: 'Member patient number',
  },
];

// ============================================================================
// UNIFIED FIELD DEFINITION (Core + Custom fields)
// ============================================================================

export interface UnifiedField {
  id?: number; // undefined for core fields
  name: string;
  type: FieldType;
  friendly_name: string;
  category: string;
  mandatory: boolean;
  nullable: boolean;
  hidden: boolean;
  default_value?: string;
  str_len?: number;
  is_core: boolean; // true for core fields, false for custom
  description?: string;
  field_options?: Array<{ value: string; label: string }> | null;
  // Conditional visibility configured on the master custom field
  // (com_fields.visibility_rules). Carried into form templates so the renderer
  // hides imported fields until their conditions are satisfied.
  visibility_rules?: {
    logic: 'AND' | 'OR';
    conditions: Array<{ field: string; operator: string; value?: any }>;
    when_hidden?: 'hide' | 'disable';
  } | null;
}

// ============================================================================
// FORM FIELD TYPE MAPPING
// ============================================================================

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'time'
  | 'datetime'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'file'
  | 'signature'
  | 'section_header'
  | 'rich_text';

/**
 * Maps custom field types to form field types.
 * Choice types (select/radio/checkbox/multi_select/toggle) preserve their nature
 * so imported fields render as dropdowns/radio groups, not plain text.
 */
export function mapFieldTypeToFormFieldType(fieldType: FieldType): FormFieldType {
  const mapping: Record<FieldType, FormFieldType> = {
    str: 'text',
    long_text: 'textarea',
    int: 'number',
    bool: 'checkbox',
    date: 'date',
    datetime: 'datetime',
    time: 'time',
    fixed_date: 'date',
    select: 'select',
    multi_select: 'checkbox',
    radio: 'radio',
    checkbox: 'checkbox',
    toggle: 'checkbox',
  };
  return mapping[fieldType] || 'text';
}

/**
 * Maps form field type back to field type
 */
export function mapFormFieldTypeToFieldType(formFieldType: FormFieldType): FieldType | null {
  const mapping: Partial<Record<FormFieldType, FieldType>> = {
    text: 'str',
    textarea: 'long_text',
    number: 'int',
    date: 'date',
    datetime: 'datetime',
    time: 'time',
    select: 'select',
    radio: 'radio',
    checkbox: 'checkbox',
  };
  return mapping[formFieldType] || null;
}

// ============================================================================
// UNIFIED FIELD SERVICE
// ============================================================================

export class UnifiedFieldService {
  /**
   * Combines core and custom fields into a unified list
   */
  static getAllFields(customFields: CustomField[]): UnifiedField[] {
    // Convert core fields
    const coreFields: UnifiedField[] = CORE_FIELDS.map((field) => ({
      ...field,
      hidden: false,
      is_core: true,
    }));

    // Convert custom fields
    const customFieldsUnified: UnifiedField[] = customFields.map((field) => ({
      id: field.id,
      name: field.name,
      type: field.type,
      friendly_name: field.friendly_name,
      category: field.category || 'custom',
      mandatory: field.mandatory,
      nullable: field.nullable,
      hidden: field.hidden,
      default_value: field.default_value,
      str_len: field.str_len,
      is_core: false,
      field_options: field.field_options || null,
      visibility_rules: field.visibility_rules || null,
    }));

    return [...coreFields, ...customFieldsUnified];
  }

  /**
   * Get field by name (searches both core and custom)
   */
  static getFieldByName(
    fieldName: string,
    customFields: CustomField[]
  ): UnifiedField | undefined {
    const allFields = this.getAllFields(customFields);
    return allFields.find((field) => field.name === fieldName);
  }

  /**
   * Get fields by category
   */
  static getFieldsByCategory(
    category: string,
    customFields: CustomField[]
  ): UnifiedField[] {
    const allFields = this.getAllFields(customFields);
    return allFields.filter((field) => field.category === category);
  }

  /**
   * Get all categories
   */
  static getAllCategories(customFields: CustomField[]): string[] {
    const allFields = this.getAllFields(customFields);
    const categories = new Set(allFields.map((field) => field.category));
    return Array.from(categories).sort();
  }

  /**
   * Get only visible fields (non-hidden)
   */
  static getVisibleFields(customFields: CustomField[]): UnifiedField[] {
    return this.getAllFields(customFields).filter((field) => !field.hidden);
  }

  /**
   * Get only mandatory fields
   */
  static getMandatoryFields(customFields: CustomField[]): UnifiedField[] {
    return this.getAllFields(customFields).filter((field) => field.mandatory);
  }

  /**
   * Convert unified field to form field format
   */
  static convertToFormField(field: UnifiedField) {
    const formField: any = {
      field_id: field.name,
      field_type: mapFieldTypeToFormFieldType(field.type),
      label: field.friendly_name,
      description: field.description || '',
      required: field.mandatory,
      default_value: field.default_value,
      validation: {
        max_length: field.str_len,
      },
      // Auto-populate data source for core fields
      data_source: field.is_core ? `member.${field.name}` : `member.custom_fields.${field.name}`,
    };

    // Carry over field options for select/radio/checkbox fields
    if (field.field_options && field.field_options.length > 0) {
      formField.options = field.field_options.map((opt) => opt.label);
    }

    // Carry conditional visibility from the master field config so the
    // renderer hides this field until its dependencies are satisfied. The
    // backend also reattaches this on save via _sync_field_snapshots, but
    // setting it here keeps the form-builder preview in sync immediately
    // after import.
    if (field.visibility_rules) {
      formField.visibility_rules = field.visibility_rules;
    }

    return formField;
  }

  /**
   * Extract field value from member data (handles both core and custom fields)
   */
  static extractFieldValue(fieldName: string, memberData: Member): any {
    // Check core fields first
    if (fieldName in memberData) {
      return (memberData as any)[fieldName];
    }

    // Check custom fields
    if (memberData.custom_fields && fieldName in memberData.custom_fields) {
      return memberData.custom_fields[fieldName];
    }

    return undefined;
  }

  /**
   * Prepopulate form data from member profile
   */
  static prepopulateFormFromMember(
    memberData: Member,
    customFields: CustomField[]
  ): Record<string, any> {
    const allFields = this.getAllFields(customFields);
    const prepopulatedData: Record<string, any> = {};

    allFields.forEach((field) => {
      const value = this.extractFieldValue(field.name, memberData);
      if (value !== undefined && value !== null) {
        prepopulatedData[field.name] = value;
      } else if (field.default_value) {
        prepopulatedData[field.name] = field.default_value;
      }
    });

    return prepopulatedData;
  }

  /**
   * Validate field value against field definition
   */
  static validateFieldValue(
    field: UnifiedField,
    value: any
  ): { valid: boolean; error?: string } {
    // Check mandatory
    if (field.mandatory && (value === undefined || value === null || value === '')) {
      return { valid: false, error: `${field.friendly_name} is required` };
    }

    // Check nullable
    if (!field.nullable && (value === null || value === '')) {
      return { valid: false, error: `${field.friendly_name} cannot be empty` };
    }

    // Skip validation if empty and not mandatory
    if (value === undefined || value === null || value === '') {
      return { valid: true };
    }

    // Type validation
    switch (field.type) {
      case 'str':
      case 'long_text':
        if (typeof value !== 'string') {
          return { valid: false, error: `${field.friendly_name} must be text` };
        }
        if (field.str_len && value.length > field.str_len) {
          return {
            valid: false,
            error: `${field.friendly_name} must be at most ${field.str_len} characters`,
          };
        }
        break;

      case 'int':
        if (typeof value !== 'number' && isNaN(Number(value))) {
          return { valid: false, error: `${field.friendly_name} must be a number` };
        }
        break;

      case 'bool':
        if (typeof value !== 'boolean') {
          return { valid: false, error: `${field.friendly_name} must be true or false` };
        }
        break;

      case 'date':
      case 'datetime':
      case 'time':
      case 'fixed_date':
        // Basic date format validation
        if (typeof value === 'string' && isNaN(Date.parse(value))) {
          return { valid: false, error: `${field.friendly_name} must be a valid date` };
        }
        break;
    }

    return { valid: true };
  }

  /**
   * Validate multiple field values
   */
  static validateFields(
    fields: UnifiedField[],
    values: Record<string, any>
  ): { valid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};

    fields.forEach((field) => {
      const value = values[field.name];
      const validation = this.validateFieldValue(field, value);

      if (!validation.valid && validation.error) {
        errors[field.name] = validation.error;
      }
    });

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Get field display value (formatted for display)
   */
  static getFieldDisplayValue(field: UnifiedField, value: any): string {
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    switch (field.type) {
      case 'bool':
        return value ? 'Yes' : 'No';

      case 'date':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return String(value);
        }

      case 'datetime':
        try {
          return new Date(value).toLocaleString();
        } catch {
          return String(value);
        }

      case 'time':
        return String(value);

      default:
        return String(value);
    }
  }
}

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

export {
  CustomField,
  FieldType,
  Member,
};
